import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const PRESETS = [
  { id: "pack_10", amount: 10, label: "10€", sub: "Un Carnet de Résilience" },
  { id: "pack_25", amount: 25, label: "25€", sub: "Un atelier d'expression" },
  { id: "pack_50", amount: 50, label: "50€", sub: "Une Cape Symbolique" },
  { id: "pack_100", amount: 100, label: "100€", sub: "Une semaine d'accueil" },
];

export default function Donation() {
  const [selected, setSelected] = useState("pack_50");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const isCustom = selected === "custom";

  const handleDonate = async (e) => {
    e.preventDefault();
    if (isCustom) {
      const n = Number(customAmount);
      if (!n || n < 2) {
        toast.error("Le don minimum est de 2€.");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        donor_name: name || null,
        donor_email: email || null,
        message: message || null,
      };
      if (isCustom) {
        payload.custom_amount = Number(customAmount);
      } else {
        payload.package_id = selected;
      }

      const res = await axios.post(`${API}/donations/create-checkout`, payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("Réponse invalide");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Une erreur est survenue.";
      toast.error(detail);
      setLoading(false);
    }
  };

  return (
    <section
      id="don"
      data-testid="donation-section"
      className="relative bg-[#4A192C] text-[#FAF7F2] py-24 lg:py-36 sdm-grain overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-6 relative z-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="sdm-divider text-[#C5A059]">Faire un don</p>
          <h2 className="font-serif-display text-4xl lg:text-6xl mt-8 leading-tight">
            Offrez une nuit,
            <br />
            offrez <em className="text-[#C5A059]">une Renaissance.</em>
          </h2>
          <p className="mt-6 text-[#FAF7F2]/80 leading-relaxed text-lg">
            Votre don finance directement l&apos;accueil, les ateliers, les
            cérémonies de renaissance et l&apos;incubateur de projets.
            Paiement sécurisé via Stripe.
          </p>

          <div className="mt-10 space-y-4 border-t border-[#C5A059]/30 pt-8">
            {[
              "Reçu fiscal pour les dons éligibles",
              "Paiement 100 % sécurisé",
              "Vos données restent confidentielles",
            ].map((b) => (
              <div key={b} className="flex items-start gap-3 text-[#FAF7F2]/80">
                <span className="sdm-ornament mt-2 bg-[#C5A059]" />
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={handleDonate}
            data-testid="donation-form"
            className="bg-[#FAF7F2] text-[#2C1E23] p-8 lg:p-12 space-y-8"
          >
            <div>
              <span className="sdm-label">Choisissez votre geste</span>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected(p.id)}
                    data-testid={`preset-${p.id}`}
                    className={`p-4 text-left border transition-all ${
                      selected === p.id
                        ? "border-[#4A192C] bg-[#4A192C] text-[#FAF7F2]"
                        : "border-[#4A192C]/20 hover:border-[#4A192C]"
                    }`}
                  >
                    <div className="font-serif-display text-2xl">{p.label}</div>
                    <div className="text-[0.7rem] mt-1 opacity-80">{p.sub}</div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSelected("custom")}
                data-testid="preset-custom"
                className={`mt-3 w-full p-4 text-left border transition-all ${
                  isCustom
                    ? "border-[#4A192C] bg-[#4A192C] text-[#FAF7F2]"
                    : "border-[#4A192C]/20 hover:border-[#4A192C]"
                }`}
              >
                <div className="font-serif-display text-lg">
                  Un autre montant
                </div>
                <div className="text-[0.7rem] mt-1 opacity-80">
                  Choisissez librement
                </div>
              </button>
              {isCustom && (
                <div className="mt-4">
                  <label className="sdm-label" htmlFor="custom-amount">
                    Montant (€)
                  </label>
                  <input
                    id="custom-amount"
                    data-testid="custom-amount-input"
                    type="number"
                    min="2"
                    step="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="75"
                    className="sdm-input"
                    required
                  />
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="sdm-label" htmlFor="donor-name">
                  Votre nom (optionnel)
                </label>
                <input
                  id="donor-name"
                  data-testid="donor-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Marie Dupont"
                  className="sdm-input"
                />
              </div>
              <div>
                <label className="sdm-label" htmlFor="donor-email">
                  Email (pour le reçu)
                </label>
                <input
                  id="donor-email"
                  data-testid="donor-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="marie@exemple.fr"
                  className="sdm-input"
                />
              </div>
            </div>

            <div>
              <label className="sdm-label" htmlFor="donor-message">
                Un mot pour les Reines (optionnel)
              </label>
              <textarea
                id="donor-message"
                data-testid="donor-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Votre message de soutien..."
                className="sdm-input resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-donation-button"
              className="sdm-btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirection vers le paiement...
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4" />
                  Faire un don maintenant
                </>
              )}
            </button>
            <p className="text-xs text-[#6B585F] text-center">
              Paiement sécurisé par Stripe · Monnaie : EUR
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
